import { router } from '@/Router';
import { CookieHandler } from '@/Telemetry/cookie-init';
import { useAppStore } from '@/inertia/AppStore';
import { onSelectPlayerNode } from '@/inertia/Shared/Catalog/catalog.utils';
import { chatbotStateManager } from './chatbotStateManager';
import './customElements'; /*DO NOT REMOVE YET - REMOVING THIS IS CAUSING AN ODD ERROR WITH THE CUSTOM CHATBOT ELEMENTS (GREETING/CITATIONS). REQUIRES FURTHER INVESTIGATION */
import { conversationHistoryService } from './conversationHistoryService';
import type { CustomCitationEvent, CustomCitationPayload } from './customElements/citation';
import type { OnResponseReceived } from './event.types';
import type { DFMessenger, DFMessengerChatBubble } from './messenger.types';
import type { QueryParameters } from './query-params.types';

class Chatbot {
  private df: DFMessenger | null = null;
  private dfBubble: DFMessengerChatBubble | null = null;

  private store: ReturnType<typeof useAppStore.getState> | null = null;

  private searchFilterSpec: NonNullable<NonNullable<QueryParameters['searchConfig']>['filterSpecs']>[number] = {
    dataStores: [
      `projects/zngly-corporate/locations/global/collections/default_collection/dataStores/${window.znRuntime?.dfDsId}`,
    ],
    // Updated to include hidden_b_s posts
    filter: `post_status: ANY("publish", "hidden_b_s")`,
  };

  private queryParams: QueryParameters = {
    searchConfig: {
      filterSpecs: [this.searchFilterSpec],
      boostSpecs: [],
    },
  };

  // Store current query parameters to preserve filter specs between calls
  private currentQueryParams: QueryParameters | null = null;

  constructor() {
    this.enabledDialogflowCxMessenger();

    window.addEventListener('df-messenger-loaded', () => {
      // Initialize conversation history first, then set up event listeners
      this.initializeConversationHistory().then(() => {
        this.addEventListeners();
      });
    });

    // Listen for route changes and hide/show chatbot accordingly
    Chatbot.handleRouteChange();
    window.addEventListener('popstate', () => Chatbot.handleRouteChange());
    window.addEventListener('locationchange', () => Chatbot.handleRouteChange());
  }

  /**
   * By default, the chatbot is hidden
   */
  private static CHATBOT_HIDDEN = true;

  public static setChatbotHidden(hidden: boolean): void {
    Chatbot.CHATBOT_HIDDEN = hidden;

    // Update visibility immediately based on new setting
    Chatbot.handleRouteChange();

    // If chatbot is being enabled and auto-open is enabled, trigger auto-open
    if (!hidden) {
      const dfElement = document.querySelector('df-messenger') as HTMLElement;
      const shouldAutoOpen = dfElement?.getAttribute('data-auto-open') !== 'false';

      if (shouldAutoOpen) {
        // Trigger auto-open by finding the chatbot instance
        setTimeout(() => {
          // Find the df-messenger element and trigger auto-open logic directly
          const dfElement = document.querySelector('df-messenger') as HTMLElement;
          const dfBubble = document.querySelector('df-messenger-chat-bubble') as DFMessengerChatBubble;
          const displayMode = dfElement?.getAttribute('data-display-mode') || 'popup';

          if (displayMode === 'popup' && dfBubble?.openChat) {
            dfBubble.openChat();
          } else if (displayMode === 'sidebar') {
            // Show sidebar by setting visibility directly
            const sidebarMessenger = document.getElementById('sidebar-messenger');
            if (sidebarMessenger) {
              sidebarMessenger.style.visibility = 'visible';
            }
          }
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }

  private static handleRouteChange(): void {
    const shouldHide = Chatbot.shouldHideChatbot();
    const dfElement = document.querySelector('df-messenger') as HTMLElement;
    const displayMode = dfElement?.getAttribute('data-display-mode') || 'popup';

    if (displayMode === 'popup') {
      // Handle popup mode visibility
      if (dfElement) {
        if (shouldHide) {
          dfElement.style.display = 'none';
        } else {
          dfElement.style.display = '';
        }
      }
    } else if (displayMode === 'sidebar') {
      // Handle sidebar mode visibility using our new IDs
      const sidebarToggle = document.getElementById('sidebar-toggle');
      const sidebarMessenger = document.getElementById('sidebar-messenger');

      if (shouldHide) {
        // Hide both toggle button and messenger
        if (sidebarToggle) sidebarToggle.style.display = 'none';
        if (sidebarMessenger) sidebarMessenger.style.visibility = 'hidden';
      } else {
        // Show toggle button (it will manage its own visibility based on messenger state)
        if (sidebarToggle) sidebarToggle.style.display = 'block';
        // Don't auto-show messenger, let the toggle button control it
      }
    }
  }

  private static shouldHideChatbot(): boolean {
    if (Chatbot.CHATBOT_HIDDEN === true) {
      return true;
    }

    const pathname = window.location.pathname;

    // Routes where chatbot should remain visible
    const visibleRoutes = ['/znglist/$slug', '/users/$user/znglist/$slug', '/hub/$slug', '/content/$postId'];

    const url = new URL(window.location.href);

    if (url.pathname === '/') {
      return false;
    }

    for (const route of visibleRoutes) {
      const slugRegex = new RegExp(`^${route.replace(/\$\w+/g, '[^/]+')}$`);
      if (slugRegex.test(pathname)) {
        return false;
      }
    }

    return true;
  }

  private async initializeConversationHistory() {
    try {
      // Load conversation history once to avoid multiple calls
      const conversationHistory = await conversationHistoryService.loadConversationHistory({ limit: 5 });

      const hasHistory = !!(conversationHistory && conversationHistory.totalMessages > 0);

      // Only show enhanced greeting if there's actual BigQuery history
      if (hasHistory && conversationHistory) {
        const enhancedGreeting =
          await conversationHistoryService.generateEnhancedGreetingFromHistory(conversationHistory);
        if (enhancedGreeting) {
          this.contextualGreeting = enhancedGreeting;
        }
      }
    } catch (error) {
      // Error initializing conversation history
    }
  }

  private contextualGreeting: string | null = null;

  private enabledDialogflowCxMessenger() {
    const script = document.getElementById('dialogflow-cx-messenger-script') as HTMLScriptElement;
    if (!!script?.getAttribute?.('data-src')) {
      script.type = 'text/javascript';
      const src = script.getAttribute('data-src');
      script.defer = true;
      script.async = true;
      script.setAttribute('src', src || '');
    }
  }

  private addEventListeners() {
    this.df = document.querySelector('df-messenger') as DFMessenger;
    this.dfBubble = document.querySelector('df-messenger-chat-bubble') as DFMessengerChatBubble;

    // Set initial query parameters with conversation context
    this.setDialogflowParameters();

    // Check auto-open setting and display mode from data attributes
    const autoOpenValue = this.df?.getAttribute('data-auto-open') || 'true';
    const shouldAutoOpen = autoOpenValue === 'true';
    const displayMode = this.df?.getAttribute('data-display-mode') || 'popup';

    if (shouldAutoOpen) {
      setTimeout(() => {
        if (displayMode === 'popup' && this.dfBubble?.openChat) {
          this.dfBubble.openChat();
        } else if (displayMode === 'sidebar') {
          // Show sidebar by setting visibility directly
          const sidebarMessenger = document.getElementById('sidebar-messenger');
          if (sidebarMessenger) {
            sidebarMessenger.style.visibility = 'visible';
          }
        }

        // Show contextual greeting for returning users if available
        if (this.contextualGreeting) {
          this.checkAndShowContextualGreeting();
        }
      }, 1000); // 1 second delay
    } else {
      // Still show contextual greeting for returning users even if not auto-opening
      if (this.contextualGreeting) {
        setTimeout(() => {
          this.checkAndShowContextualGreeting();
        }, 2000); // Longer delay since user needs to manually open
      }
    }

    window.addEventListener('df-response-received', (event) => {
      this.onResponseReceived(event as OnResponseReceived);
    });

    // Listen for user input to log messages locally
    window.addEventListener('df-user-input-entered', (event) => {
      this.onUserInputEntered(event as OnResponseReceived);
    });
    window.addEventListener('df-custom-citation-clicked', (event) => {
      this.onDfCustomCitationClicked(event as CustomCitationEvent);
    });

    // location change event
    const onLocationChange = () => {
      this.store = useAppStore.getState();
    };
    window.addEventListener('locationchange', onLocationChange);

    // remove event listener on unmount
    window.addEventListener('beforeunload', () => {
      window.removeEventListener('locationchange', onLocationChange);
    });
  }

  // Greeting management methods
  public hasGreetingBeenShown(): boolean {
    return chatbotStateManager.hasGreetingBeenShown();
  }

  public markGreetingAsShown(): void {
    chatbotStateManager.markGreetingAsShown();
  }

  public resetGreeting(): void {
    chatbotStateManager.resetGreeting();
  }

  // Session management methods
  public getSessionId(): string | null {
    return chatbotStateManager.getSessionId();
  }

  public setSessionId(id: string): void {
    chatbotStateManager.setSessionId(id);
  }

  // Check if greeting already exists in DOM
  public hasGreetingInDOM(): boolean {
    return chatbotStateManager.hasGreetingInDOM();
  }

  // Get the DFMessenger element
  public getDFMessenger(): DFMessenger | null {
    return this.df;
  }

  // Clear existing greetings from the chat
  public clearExistingGreetings(): void {
    try {
      if (!this.df?.shadowRoot) {
        return;
      }

      // Try multiple strategies to find and remove greetings

      // Strategy 1: Find direct df-custom-greeting elements
      const existingGreetings = this.df.shadowRoot.querySelectorAll('df-custom-greeting');
      existingGreetings.forEach((greeting) => {
        greeting.remove();
      });

      // Strategy 2: Look in all possible message containers
      const allContainers = this.df.shadowRoot.querySelectorAll('*');

      allContainers.forEach((container) => {
        const greetingsInContainer = container.querySelectorAll('df-custom-greeting');
        if (greetingsInContainer.length > 0) {
          greetingsInContainer.forEach((greeting) => {
            greeting.remove();
          });
        }
      });

      // Strategy 3: Clear messages that contain greeting text
      const allElements = this.df.shadowRoot.querySelectorAll('*');
      allElements.forEach((element) => {
        const text = element.textContent || '';
        if (text.includes('Welcome to') && text.includes('!') && element.tagName !== 'DF-CUSTOM-GREETING') {
          element.remove();
        }
      });
    } catch (error) {
      // Silent error handling
    }
  }

  // Wait for chat to be ready
  public waitForChatReady(): Promise<boolean> {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 20;

      const checkReady = () => {
        attempts++;

        if (!this.df) {
          this.df = document.querySelector('df-messenger') as DFMessenger;
        }

        if (this.df?.shadowRoot) {
          resolve(true);
        } else if (attempts < maxAttempts) {
          setTimeout(checkReady, 100);
        } else {
          resolve(false);
        }
      };

      checkReady();
    });
  }

  private onDfCustomCitationClicked(event: CustomCitationEvent) {
    if (!event?.detail?.citation?.anchor?.href) {
      return;
    }

    try {
      const url = new URL(event.detail.citation.anchor.href);

      const { origin, pathname, searchParams } = url;

      if (origin !== window.location.origin) {
        // Open external links in a new tab
        return;
      }

      // Handle internal links
      if (searchParams.has('c')) {
        const contentId = searchParams.get('c');
        if (contentId) {
          this.navigateToContentId(contentId);
          return;
        }
      }

      // if path like /content/${id}
      const contentRegex = /\/content\/([a-zA-Z0-9-]+)/;
      if (pathname.match(contentRegex)) {
        const contentId = pathname.match(contentRegex)?.[1];
        if (contentId) {
          this.navigateToContentId(contentId);
          return;
        }
      }
    } catch (error) {
      // Invalid URL
      return;
    }
  }

  private navigateToContentId(contentId?: string | null | undefined) {
    if (!contentId || typeof contentId !== 'string' || contentId.trim() === '') {
      return;
    }

    const currentUrlCatalogId = new URL(window.location.href).searchParams.get('c');
    if (currentUrlCatalogId?.startsWith?.(contentId)) {
      return;
    }

    this.store = useAppStore.getState();

    if (this.store?.pageType === 'hub') {
      const hub = this.store?.hub;

      let hubPageId = null as string | null | undefined;
      let catalogId = null as string | null | undefined;

      hub?.connectionHubToHps?.edges?.forEach((edge) => {
        if (!edge?.node || !!hubPageId) return;
        const hubPageNode = edge?.node as NonNullable<
          NonNullable<typeof hub>['connectionHubToHps']
        >['edges'][number]['node'];

        // loop through the hub pages and find the catalog id
        hubPageNode.hubPage?.node?.connectionHpToCs?.edges?.forEach((edge) => {
          if (!edge?.node || !!catalogId) return;
          const catalogNode = edge?.node as NonNullable<
            NonNullable<typeof hubPageNode.hubPage.node>['connectionHpToCs']
          >['edges'][number]['node'];

          if (catalogNode?.catalogId?.startsWith(contentId)) {
            catalogId = catalogNode?.catalogId;
            hubPageId = hubPageNode?.pageId;

            return;
          }
        });
      });

      if (catalogId && hubPageId) {
        router.navigate({
          // @ts-ignore
          search: (prev) => {
            return {
              ...prev,
              c: catalogId,
              nav: hubPageId,
            };
          },
        });

        return;
      }
    }

    const foundNode = this.store.nodes.find((node) => {
      return node?.id?.startsWith(contentId);
    });

    if (!foundNode) {
      // maybe navigate to the single page, add some form of args for this to be an option
      router.navigate({
        to: '/content/$postId',
        params: {
          postId: contentId,
        },
        search: {
          c: contentId,
        },
      });

      return;
    }

    onSelectPlayerNode(foundNode, 0);
  }

  private onResponseReceived(event: OnResponseReceived) {
    // Enhanced version that includes local logging
    this.handleResponse(event);
  }

  /**
   * Handle response with local logging
   */
  private async handleResponse(event: OnResponseReceived) {
    // Call original response handler first
    this.originalOnResponseReceived(event);

    try {
      const response = event.detail;
      if (response?.messages?.length) {
        const fulfillmentText = response.messages
          .filter((msg) => msg.type === 'text' && 'text' in msg)
          .map((msg) => (msg as { text: string }).text)
          .join(' ');

        if (fulfillmentText) {
          // Extract properties from raw response if available
          const queryResult = response.raw?.queryResult as Record<string, unknown> | undefined;

          await chatbotStateManager.logMessage({
            text: fulfillmentText,
            sender: 'bot',
            dialogflowResponse: {
              queryText: (queryResult?.queryText as string) || '',
              fulfillmentText,
              languageCode: (queryResult?.languageCode as string) || 'en',
              sessionId: response.raw?.responseId || '',
              responseId: response.raw?.responseId || '',
            },
            intentName: (queryResult?.intent as { displayName?: string })?.displayName,
            confidenceScore: queryResult?.intentDetectionConfidence as number,
          });

          // Update session summary periodically
          this.updateSessionSummary({
            intent: { displayName: (queryResult?.intent as { displayName?: string })?.displayName },
            query: (queryResult?.queryText as string) || '',
            intentDetectionConfidence: queryResult?.intentDetectionConfidence as number,
          });

          // Update session parameters after each exchange
          await this.setDialogflowParameters();
        }
      }
    } catch (error) {
      // Error logging bot response
    }
  }

  /**
   * Original response handler (renamed to avoid conflicts)
   */
  private originalOnResponseReceived(event: OnResponseReceived) {
    event.preventDefault();

    if (!event.detail?.messages?.length) {
      return;
    }

    const texts = event.detail.messages.filter((message) => {
      return message.type === 'text';
    });

    if (texts.length > 0) {
      for (const text of texts) {
        this.df?.renderCustomText((text as { text?: string }).text || '', true);
      }
    }

    const citationObjects = event.detail.messages.filter((message) => {
      return message.type === 'match_citations';
    });

    if (citationObjects.length > 0) {
      this.df?.renderCustomCard(
        citationObjects.map((obj) => {
          return {
            type: 'custom_template',
            name: 'df-custom-citation',
            payload: JSON.stringify({
              citations: (obj.citations || []).map((c) => {
                return {
                  title: c.title,
                  subtitle: c.subtitle,
                  anchor: {
                    href: c.actionLink,
                  },
                };
              }),
            } as CustomCitationPayload),
          };
        })
      );
    }
  }

  /**
   * Update stored query parameters (called from useDfMessenger when filter changes)
   */
  public updateQueryParameters(params: QueryParameters): void {
    this.currentQueryParams = params;
  }

  /**
   * Set Dialogflow parameters including conversation context
   */
  private async setDialogflowParameters(): Promise<void> {
    try {
      // Use stored current parameters to preserve existing filter specs, fallback to default
      const baseParams = this.currentQueryParams || this.queryParams;
      const enhancedParams = { ...baseParams };

      // Wait for Google Analytics cookie to be ready to get consistent user identifier
      await CookieHandler.waitForGACookie(5000);

      // Add conversation context parameters
      const conversationContext = chatbotStateManager.getConversationContext();
      const userIdentifier = chatbotStateManager.getUserIdentifier();

      // Load conversation history to get session info
      const history = await chatbotStateManager.loadConversationHistory();

      // Get BigQuery conversation history for enhanced parameters
      const bigQueryParams = await conversationHistoryService.generateSessionParameters();
      const conversationContextString = await conversationHistoryService.getConversationContextString();
      const hasHistory = await conversationHistoryService.hasConversationHistory();

      // Add session parameters for Dialogflow
      enhancedParams.parameters = {
        // User identification
        user_identifier: userIdentifier,

        // BigQuery conversation history parameters
        conversation_history: bigQueryParams.conversationHistory || null,
        previous_topics: bigQueryParams.previousTopics || null,
        last_interaction: bigQueryParams.lastInteraction || null,
        session_context: bigQueryParams.sessionContext || null,
        conversation_context: conversationContextString,
        has_conversation_history: hasHistory,

        // Determine user type - use BigQuery enhanced or fallback to identifier check
        user_type: bigQueryParams.userType || (userIdentifier.startsWith('anonymous-') ? 'anonymous' : 'authenticated'),

        // Legacy local context (fallback)
        is_returning_user: !!(history?.isActive && history.messageCount && history.messageCount > 0),
        last_conversation_summary: history?.lastMessageSummary || null,
        message_count: history?.messageCount || 0,
        last_activity: history?.lastActivityAt || null,
        last_intent: conversationContext?.lastIntent || null,
        last_topic: conversationContext?.lastTopic || null,
        topics_discussed: conversationContext?.topics ? conversationContext.topics.join(',') : null,

        // Session metadata
        page_context: window.location.pathname,
        session_start_time: new Date().toISOString(),

        // Contextual greeting flag
        has_contextual_greeting: !!this.contextualGreeting,
      };

      // Set the enhanced parameters
      this.df?.setQueryParameters(enhancedParams);
    } catch (error) {
      // Fallback to stored current parameters or basic parameters, preserving filter specs
      const fallbackParams = this.currentQueryParams || this.queryParams;
      this.df?.setQueryParameters(fallbackParams);
    }
  }

  /**
   * Handle user input to log messages locally
   */
  private async onUserInputEntered(event: CustomEvent) {
    try {
      const userInput = event.detail?.input;
      if (!userInput) return;

      // Ensure we have a local session created
      await this.ensureLocalSession();

      await chatbotStateManager.logMessage({
        text: userInput,
        sender: 'user',
      });

      // Update session parameters before sending to Dialogflow
      await this.setDialogflowParameters();
    } catch (error) {
      // Error logging user input
    }
  }

  /**
   * Ensure local session exists
   */
  private async ensureLocalSession(): Promise<void> {
    try {
      const sessionId = chatbotStateManager.getSessionId();
      if (!sessionId) {
        // Generate a new Dialogflow session ID
        const newSessionId = `projects/zngly-corporate/locations/global/agents/${(window.znRuntime as Record<string, unknown>)?.dfAgentId}/sessions/${this.generateSessionId()}`;
        chatbotStateManager.setSessionId(newSessionId);

        // Create session locally
        await chatbotStateManager.createServerSession(newSessionId);

        // Update Dialogflow parameters with new session info
        await this.setDialogflowParameters();
      }
    } catch (error) {
      // Error ensuring local session
    }
  }

  /**
   * Generate a unique session ID using cryptographically secure random values
   */
  private generateSessionId(): string {
    // Use Web Crypto API for secure random values
    const array = new Uint8Array(8);
    crypto.getRandomValues(array);
    const randomPart = Array.from(array, (byte) => byte.toString(36))
      .join('')
      .substring(0, 11);
    return `${Date.now()}-${randomPart}`;
  }

  /**
   * Update session summary based on conversation context
   */
  private async updateSessionSummary(response: {
    intent?: { displayName?: string };
    query?: string;
    intentDetectionConfidence?: number;
  }): Promise<void> {
    try {
      // Simple logic to create summary - could be enhanced with AI
      const intent = response.intent?.displayName;
      if (intent && intent !== 'Default Welcome Intent') {
        const summary = this.generateSummaryFromIntent(intent, response.query || '');

        const context = {
          lastIntent: intent,
          lastTopic: this.extractTopicFromQuery(response.query || ''),
          messageCount: (chatbotStateManager.getConversationContext()?.messageCount || 0) + 1,
          averageConfidence: response.intentDetectionConfidence,
        };

        await chatbotStateManager.updateSessionSummary(summary, context);
      }
    } catch (error) {
      // Error updating session summary
    }
  }

  /**
   * Generate summary from intent and query
   */
  private generateSummaryFromIntent(intent: string, query: string): string {
    // Simple mapping of intents to human-readable summaries
    const intentMap: Record<string, string> = {
      'search.content': 'content search',
      'ask.question': 'general questions',
      'get.help': 'help and support',
      'find.podcast': 'client podcasts',
      'find.video': 'video content',
      'navigate.page': 'site navigation',
    };

    return intentMap[intent] || query.substring(0, 50);
  }

  /**
   * Extract topic from user query
   */
  private extractTopicFromQuery(query: string): string {
    // Simple keyword extraction - could be enhanced with NLP
    const keywords = ['podcast', 'video', 'content', 'client', 'help', 'search'];
    const foundKeyword = keywords.find((keyword) => query.toLowerCase().includes(keyword));
    return foundKeyword || 'general';
  }

  /**
   * Get contextual greeting for returning users
   */
  public getContextualGreeting(): string | null {
    return this.contextualGreeting;
  }

  /**
   * Show contextual greeting if available
   */
  public async showContextualGreeting(): Promise<void> {
    if (this.contextualGreeting && this.df) {
      // Use renderCustomText to add contextual message alongside existing greeting
      this.df.renderCustomText(this.contextualGreeting, true);
    }
  }

  /**
   * Check if user should see contextual greeting and show it
   */
  public async checkAndShowContextualGreeting(): Promise<void> {
    try {
      // Wait for chat to be ready
      await this.waitForChatReady();

      // Small delay to ensure existing greeting is rendered first
      setTimeout(() => {
        this.showContextualGreeting();
      }, 1000);
    } catch (error) {
      // Error showing contextual greeting
    }
  }

  /**
   * Trigger auto-open when chatbot is enabled
   */
  public triggerAutoOpen(): void {
    const displayMode = this.df?.getAttribute('data-display-mode') || 'popup';

    // In popup mode, open the chat bubble. In sidebar mode, use the sidebar control
    if (displayMode === 'popup' && this.dfBubble?.openChat) {
      this.dfBubble.openChat();
    } else if (displayMode === 'sidebar') {
      // Show sidebar directly using our element ID
      const sidebarMessenger = document.getElementById('sidebar-messenger');
      if (sidebarMessenger) {
        sidebarMessenger.style.visibility = 'visible';
      }
    }

    // Show contextual greeting for returning users if available
    if (this.contextualGreeting) {
      this.checkAndShowContextualGreeting();
    }
  }
}

// Export singleton instance
export const chatbotInstance = new Chatbot();

export { Chatbot };

export default {};
